// Lucian UX/UI Prompt #097 · MIT License (c) 2026 Seokwoooo · https://lucianlabs.dev/uxui-prompt/#097
// 割合のドーナツ。切れ目や凡例に触れると外へ少し出て太くなり、真ん中がその項目の件数と割合に変わる。
// 押すとその項目を外し、残りで円を割り直す（もう一度押すと戻る）。凡例に件数を出すので色だけに頼らない。
// 色は上位3つまで（暗い面で全ペアを見分けられるのは3色。ADR 0027）。4つ目以降は「その他」にまとめる。
import React, { useMemo, useState } from 'react';
import { foldDonut } from '../../utils/home-viz.mjs';
import './lucian.css';

const SLOT = ['var(--lx-cat-0)', 'var(--lx-cat-1)', 'var(--lx-cat-2)'];
const colorOf = (d) => (d.other ? 'var(--lx-cat-other)' : SLOT[d.slot]);
const R = 58, C = 2 * Math.PI * R, GAP = 2;

export default function DonutBreakdown({ rows, unit = '件', label }) {
  const items = useMemo(() => foldDonut(rows, 3), [rows]);
  const [off, setOff] = useState({});
  const [hot, setHot] = useState(null);
  const on = items.filter(d => !off[d.label]);
  const total = on.reduce((s, d) => s + d.n, 0);
  let acc = 0;
  const arcs = on.map(d => {
    const len = total > 0 ? (d.n / total) * C : 0;
    const a = { d, len, start: acc };
    acc += len;
    return a;
  });
  const h = hot ? on.find(d => d.label === hot) : null;
  const toggle = (l) => setOff(o => {
    const next = { ...o, [l]: !o[l] };
    return items.every(d => next[d.label]) ? o : next; // 全部は外せない
  });

  return (
    <div className="lx-donut">
      <svg viewBox="0 0 160 160" role="img" aria-label={`${label}。${on.map(d => `${d.label} ${d.n}${unit}`).join('、')}`}>
        <g transform="rotate(-90 80 80)">
          {arcs.map(({ d, len, start }) => {
            const mid = ((start + len / 2) / C) * 2 * Math.PI;
            const isHot = hot === d.label;
            return (
              <circle key={d.label} className={`lx-donut__slice${isHot ? ' is-hot' : ''}`} cx="80" cy="80" r={R}
                stroke={colorOf(d)} strokeDasharray={`${Math.max(0, len - GAP)} ${C}`} strokeDashoffset={-start}
                style={{ transform: isHot ? `translate(${Math.cos(mid) * 5}px, ${Math.sin(mid) * 5}px)` : 'none' }}
                onPointerEnter={() => setHot(d.label)} onPointerLeave={() => setHot(null)} onClick={() => toggle(d.label)} />
            );
          })}
        </g>
        <text x="80" y="80" textAnchor="middle" className="lx-donut__center-n">{h ? h.n : total}</text>
        <text x="80" y="100" textAnchor="middle" className="lx-donut__center-l">
          {h ? `${h.label} ${total ? Math.round((h.n / total) * 100) : 0}%` : '合計'}
        </text>
      </svg>
      <div className="lx-legend">
        {items.map(d => (
          <button key={d.label} type="button" aria-pressed={!off[d.label]} className={hot === d.label ? 'is-hot' : ''}
            onPointerEnter={() => setHot(off[d.label] ? null : d.label)} onPointerLeave={() => setHot(null)}
            onFocus={() => setHot(off[d.label] ? null : d.label)} onBlur={() => setHot(null)} onClick={() => toggle(d.label)}>
            <span className="lx-key" style={{ background: colorOf(d) }} />
            <span>{d.label}</span>
            <span className="lx-legend__n">{d.n}{unit}{total && !off[d.label] ? `（${Math.round((d.n / total) * 100)}%）` : ''}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
